/**
 * Data Room Utilities
 *
 * Functions for calculating progress, coverage, and confidence scores.
 */

import { DATA_ROOM_DOCUMENTS } from './documents';
import type {
  CategoryProgress,
  DataRoomDocument,
  DataRoomProgress,
  DimensionCoverage,
  DocumentCategory,
  DocumentRequirement,
  DocumentStatus,
  DocumentUpload,
  FundingStage,
  ScoringDimension,
} from './types';
import { CATEGORY_LABELS, DIMENSION_LABELS, EVIDENCE_MULTIPLIERS, STAGE_WEIGHTS } from './types';

/**
 * Check if a document requirement is "required" for a stage
 */
export function isDocumentRequired(
  requirement: DocumentRequirement
): boolean {
  return requirement === 'required' || requirement === 'basic';
}

/**
 * Check if a document is applicable for a stage (not 'none')
 */
export function isDocumentApplicable(
  requirement: DocumentRequirement
): boolean {
  return requirement !== 'none';
}

/**
 * Get the requirement level text for display
 */
export function getRequirementLabel(requirement: DocumentRequirement): string {
  switch (requirement) {
    case 'required':
      return 'Required';
    case 'basic':
      return 'Basic Required';
    case 'recommended':
      return 'Recommended';
    case 'if-any':
      return 'If Applicable';
    case 'if-required':
      return 'If Required';
    case 'none':
      return 'Not Required';
    default:
      return requirement;
  }
}

/**
 * Get status display label
 */
export function getStatusLabel(status: DocumentStatus): string {
  switch (status) {
    case 'not-started':
      return 'Not Started';
    case 'in-progress':
      return 'In Progress';
    case 'uploaded':
      return 'Uploaded';
    case 'verified':
      return 'Verified';
    case 'not-applicable':
      return 'N/A';
    default:
      return status;
  }
}

/**
 * Get status color
 */
export function getStatusColor(status: DocumentStatus): {
  bg: string;
  text: string;
  border: string;
} {
  switch (status) {
    case 'verified':
      return { bg: '#dcfce7', text: '#166534', border: '#86efac' };
    case 'uploaded':
      return { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' };
    case 'in-progress':
      return { bg: '#fef3c7', text: '#92400e', border: '#fcd34d' };
    case 'not-applicable':
      return { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' };
    case 'not-started':
    default:
      return { bg: '#fef2f2', text: '#991b1b', border: '#fca5a5' };
  }
}

/**
 * Get requirement priority color
 */
export function getRequirementColor(requirement: DocumentRequirement): {
  bg: string;
  text: string;
} {
  switch (requirement) {
    case 'required':
      return { bg: '#fee2e2', text: '#dc2626' };
    case 'basic':
      return { bg: '#ffedd5', text: '#ea580c' };
    case 'recommended':
      return { bg: '#dbeafe', text: '#2563eb' };
    case 'if-any':
    case 'if-required':
      return { bg: '#f3f4f6', text: '#6b7280' };
    case 'none':
    default:
      return { bg: '#f9fafb', text: '#9ca3af' };
  }
}

/**
 * Calculate overall data room progress
 */
export function calculateProgress(
  uploads: Record<string, DocumentUpload>,
  stage: FundingStage
): DataRoomProgress {
  const applicableDocs = DATA_ROOM_DOCUMENTS.filter((doc) =>
    isDocumentApplicable(doc.requirements[stage])
  );

  const requiredDocs = applicableDocs.filter((doc) =>
    isDocumentRequired(doc.requirements[stage])
  );

  let uploadedCount = 0;
  let verifiedCount = 0;
  let totalConfidence = 0;

  for (const doc of applicableDocs) {
    const upload = uploads[doc.id];
    const status = upload?.status || 'not-started';

    if (status === 'uploaded' || status === 'verified') {
      uploadedCount++;
    }
    if (status === 'verified') {
      verifiedCount++;
    }

    // Calculate confidence contribution
    totalConfidence += getConfidenceMultiplier(status);
  }

  const completionPercentage =
    requiredDocs.length > 0
      ? Math.round(
          (requiredDocs.filter((doc) => {
            const status = uploads[doc.id]?.status || 'not-started';
            return status === 'uploaded' || status === 'verified' || status === 'not-applicable';
          }).length /
            requiredDocs.length) *
            100
        )
      : 100;

  const avgConfidence =
    applicableDocs.length > 0 ? totalConfidence / applicableDocs.length : 1;

  return {
    totalDocuments: applicableDocs.length,
    requiredDocuments: requiredDocs.length,
    uploadedDocuments: uploadedCount,
    verifiedDocuments: verifiedCount,
    completionPercentage,
    confidenceMultiplier: Math.round(avgConfidence * 100) / 100,
  };
}

/**
 * Real deal-readiness from the startup's actual data-room checklist uploads.
 *
 * Replaces the hardcoded `MOCK_DD_DOCS` (score-page-helpers.ts) that rendered
 * unconditionally on the score page, the dashboard tile, and in scorecard PDF
 * exports. "Present" = a checklist doc whose upload status is uploaded/verified.
 * Missing docs are split by whether the doc is required (critical) at this stage.
 * Shape matches what `DealReadinessCard` + the PDF payload consume.
 */
export interface DealReadinessDoc {
  name: string;
  /** Required (critical) at this stage. */
  critical: boolean;
  /** Uploaded or verified in the data room. */
  present: boolean;
}

export interface DealReadiness {
  present: number;
  total: number;
  percent: number;
  missingCritical: Array<{ name: string }>;
  missingRecommended: Array<{ name: string }>;
  /** Full per-document list (drives the PDF export + detailed checklist). */
  docs: DealReadinessDoc[];
}

export function computeDealReadiness(
  uploads: Record<string, DocumentUpload>,
  stage: FundingStage,
): DealReadiness {
  const applicable = DATA_ROOM_DOCUMENTS.filter((doc) =>
    isDocumentApplicable(doc.requirements[stage]),
  );

  const isPresent = (id: string): boolean => {
    const status = uploads[id]?.status;
    return status === 'uploaded' || status === 'verified';
  };

  const docs: DealReadinessDoc[] = applicable.map((doc) => ({
    name: doc.name,
    critical: isDocumentRequired(doc.requirements[stage]),
    present: isPresent(doc.id),
  }));

  const total = docs.length;
  const present = docs.filter((d) => d.present).length;
  const percent = total > 0 ? Math.round((present / total) * 100) : 0;

  const missingCritical = docs
    .filter((d) => !d.present && d.critical)
    .map((d) => ({ name: d.name }));
  const missingRecommended = docs
    .filter((d) => !d.present && !d.critical)
    .map((d) => ({ name: d.name }));

  return { present, total, percent, missingCritical, missingRecommended, docs };
}

/**
 * Calculate progress by category
 */
export function calculateCategoryProgress(
  uploads: Record<string, DocumentUpload>,
  stage: FundingStage
): CategoryProgress[] {
  const categories = Object.keys(CATEGORY_LABELS) as DocumentCategory[];

  return categories.map((category) => {
    const categoryDocs = DATA_ROOM_DOCUMENTS.filter(
      (doc) => doc.category === category && isDocumentApplicable(doc.requirements[stage])
    );

    const requiredDocs = categoryDocs.filter((doc) =>
      isDocumentRequired(doc.requirements[stage])
    );

    let uploadedCount = 0;
    let verifiedCount = 0;

    for (const doc of categoryDocs) {
      const status = uploads[doc.id]?.status || 'not-started';
      if (status === 'uploaded' || status === 'verified') {
        uploadedCount++;
      }
      if (status === 'verified') {
        verifiedCount++;
      }
    }

    const percentage =
      requiredDocs.length > 0
        ? Math.round(
            (requiredDocs.filter((doc) => {
              const status = uploads[doc.id]?.status || 'not-started';
              return status === 'uploaded' || status === 'verified' || status === 'not-applicable';
            }).length /
              requiredDocs.length) *
              100
          )
        : 100;

    return {
      category,
      label: CATEGORY_LABELS[category],
      total: categoryDocs.length,
      required: requiredDocs.length,
      uploaded: uploadedCount,
      verified: verifiedCount,
      percentage,
    };
  });
}

/**
 * Calculate dimension coverage based on uploaded documents
 */
export function calculateDimensionCoverage(
  uploads: Record<string, DocumentUpload>,
  stage: FundingStage
): DimensionCoverage[] {
  const dimensions = Object.keys(DIMENSION_LABELS) as ScoringDimension[];
  const weights = STAGE_WEIGHTS[stage];

  return dimensions.map((dimension) => {
    const dimensionDocs = DATA_ROOM_DOCUMENTS.filter(
      (doc) =>
        doc.supportsDimensions.includes(dimension) &&
        isDocumentRequired(doc.requirements[stage])
    );

    const uploadedDocs = dimensionDocs.filter((doc) => {
      const status = uploads[doc.id]?.status || 'not-started';
      return status === 'uploaded' || status === 'verified';
    });

    return {
      dimension,
      label: DIMENSION_LABELS[dimension],
      weight: weights[dimension] * 100,
      documentsRequired: dimensionDocs.length,
      documentsUploaded: uploadedDocs.length,
      coveragePercentage:
        dimensionDocs.length > 0
          ? Math.round((uploadedDocs.length / dimensionDocs.length) * 100)
          : 100,
    };
  });
}

/**
 * Get confidence multiplier based on document status
 */
export function getConfidenceMultiplier(status: DocumentStatus): number {
  switch (status) {
    case 'verified':
      return EVIDENCE_MULTIPLIERS.verified;
    case 'uploaded':
      return EVIDENCE_MULTIPLIERS.uploaded;
    case 'in-progress':
      return EVIDENCE_MULTIPLIERS['in-progress'];
    case 'not-applicable':
      return EVIDENCE_MULTIPLIERS.verified; // N/A doesn't penalize
    case 'not-started':
    default:
      return EVIDENCE_MULTIPLIERS.missing;
  }
}

/**
 * Get documents filtered and sorted for display
 */
export function getFilteredDocuments(
  stage: FundingStage,
  category?: DocumentCategory,
  uploads?: Record<string, DocumentUpload>,
  showOnlyIncomplete?: boolean
): DataRoomDocument[] {
  let docs = DATA_ROOM_DOCUMENTS.filter((doc) =>
    isDocumentApplicable(doc.requirements[stage])
  );

  if (category) {
    docs = docs.filter((doc) => doc.category === category);
  }

  if (showOnlyIncomplete && uploads) {
    docs = docs.filter((doc) => {
      const status = uploads[doc.id]?.status || 'not-started';
      return status !== 'uploaded' && status !== 'verified' && status !== 'not-applicable';
    });
  }

  // Sort by requirement priority, then alphabetically
  docs.sort((a, b) => {
    const priorityOrder: Record<DocumentRequirement, number> = {
      required: 0,
      basic: 1,
      recommended: 2,
      'if-any': 3,
      'if-required': 4,
      none: 5,
    };

    const priorityA = priorityOrder[a.requirements[stage]];
    const priorityB = priorityOrder[b.requirements[stage]];

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    return a.name.localeCompare(b.name);
  });

  return docs;
}

/**
 * Get suggested next documents to upload
 */
export function getSuggestedDocuments(
  uploads: Record<string, DocumentUpload>,
  stage: FundingStage,
  limit = 5
): DataRoomDocument[] {
  // Get incomplete required documents first
  const incomplete = DATA_ROOM_DOCUMENTS.filter((doc) => {
    const status = uploads[doc.id]?.status || 'not-started';
    return (
      isDocumentRequired(doc.requirements[stage]) &&
      status !== 'uploaded' &&
      status !== 'verified' &&
      status !== 'not-applicable'
    );
  });

  // Sort by category to spread suggestions across areas
  const byCategory = new Map<DocumentCategory, DataRoomDocument[]>();
  for (const doc of incomplete) {
    const list = byCategory.get(doc.category) || [];
    list.push(doc);
    byCategory.set(doc.category, list);
  }

  // Take one from each category in round-robin
  const suggested: DataRoomDocument[] = [];
  const categories = Array.from(byCategory.keys());
  let index = 0;

  while (suggested.length < limit && suggested.length < incomplete.length) {
    const category = categories[index % categories.length];
    const categoryDocs = byCategory.get(category);
    if (categoryDocs && categoryDocs.length > 0) {
      suggested.push(categoryDocs.shift()!);
    }
    index++;
    if (index > categories.length * 10) break; // Safety limit
  }

  return suggested;
}

/**
 * Calculate estimated score impact from data room completeness
 */
export function calculateScoreImpact(
  uploads: Record<string, DocumentUpload>,
  stage: FundingStage
): {
  confidenceMultiplier: number;
  potentialScoreReduction: number;
  missingCriticalDocs: DataRoomDocument[];
} {
  const progress = calculateProgress(uploads, stage);

  // Find missing critical (required) documents
  const missingCritical = DATA_ROOM_DOCUMENTS.filter((doc) => {
    const status = uploads[doc.id]?.status || 'not-started';
    return (
      isDocumentRequired(doc.requirements[stage]) &&
      status !== 'uploaded' &&
      status !== 'verified' &&
      status !== 'not-applicable'
    );
  });

  // Calculate potential score reduction
  // Per methodology: -0.5 confidence per missing required doc, -0.2 per missing recommended
  const requiredMissingPenalty = missingCritical.length * 0.5;
  const recommendedMissing = DATA_ROOM_DOCUMENTS.filter((doc) => {
    const req = doc.requirements[stage];
    const status = uploads[doc.id]?.status || 'not-started';
    return (
      req === 'recommended' &&
      status !== 'uploaded' &&
      status !== 'verified' &&
      status !== 'not-applicable'
    );
  }).length;
  const recommendedMissingPenalty = recommendedMissing * 0.2;

  // Cap total penalty
  const totalPenalty = Math.min(requiredMissingPenalty + recommendedMissingPenalty, 5);
  const potentialScoreReduction = Math.round(totalPenalty * 10) / 10;

  return {
    confidenceMultiplier: progress.confidenceMultiplier,
    potentialScoreReduction,
    missingCriticalDocs: missingCritical.slice(0, 5), // Top 5
  };
}
